import React, { useState, memo } from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Box } from "@material-ui/core"
import Accordion from "@material-ui/core/Accordion"
import AccordionSummary from "@material-ui/core/AccordionSummary"
import AccordionDetails from "@material-ui/core/AccordionDetails"
import PropTypes from "prop-types"

import Module from "../components/Module"
import Text from "../components/Text"
import Icon from "../components/Icon"
import Link from "../components/Link"
import FluidImage from "../components/FluidImage"
import clsx from "clsx"

const useStyles = makeStyles(theme => ({
  supportModule: {
    fontFamily: "Spezia",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  containerSize: {
    maxWidth: 842,
    margin: "0 auto",
    padding: "0px 32px",
    [theme.breakpoints.down("md")]: {
      padding: "0px 16px",
    },
  },
  mainTitle: {
    textAlign: "center",
    marginBottom: 32,
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(3),
    },
  },
  accordionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  accordionItem: {
    transition: "all 0.2s ease-in-out",
    backgroundColor: "#F0F0F0",
    boxShadow: "none",
    "&:before": {
      backgroundColor: "transparent !important",
    },
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "36px",
    display: "flex",
    alignItems: "center",
    color: "black",
    [theme.breakpoints.down("md")]: {
      fontSize: 18,
      lineHeight: "27px",
    },
  },
  arrow: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "solid 2px #F29100",
    borderBottom: 0,
    borderLeft: 0,
    borderRadius: 1,
    transform: "rotate(135deg) !important",
    transition: "transform 0.3s ease",
  },
  arrowUp: {
    transform: "rotate(315deg) !important",
  },
  description: {
    padding: "8px 16px 16px 16px",
    alignSelf: "stretch",
  },
  iconsContainer: {
    marginTop: 40,
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "nowrap",
    gap: 32,
    [theme.breakpoints.down("sm")]: {
      marginTop: 28,
      flexWrap: "wrap",
      gap: 0,
      rowGap: 32,
    },
  },
  phoneSpacing: {
    maxWidth: 170,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(50% - 16px)",
    },
  },
  secondaryIcon: {
    height: 57,
    width: "auto",
    "& img": {
      objectFit: "contain",
      width: "100%",
      height: "100%",
    },
  },
  secondaryContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(3),
    opacity: 1,
    cursor: "pointer",
    "&:hover": {
      transition: "0.2s all cubic-bezier(0.075, 0.82, 0.165, 1)",
      opacity: 0.6,
    },
  },
  secondaryTitle: {
    textAlign: "center",
    textTransform: "uppercase",
    textDecoration: "underline",
    textDecorationColor: "#F29100",
    textDecorationThickness: 3,
    color: "black",
    fontSize: "0.76rem !important",
    lineHeight: "1.5 !important",
    width: "fit-content",
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px",
    },
  },
  accordionSummary: {
    padding: 16,
    margin: 0,
    "& > div": {
      margin: "0px !important",
    },
  },
  accordionDetails: {
    padding: theme.spacing(0, 0, 1, 0),
  },
}))

const AccordionItem = memo(({ question, answer, info, classes }) => {
  const [isActive, setIsActive] = useState(false)

  const handleToggle = () => {
    setIsActive(!isActive)
  }

  return (
    <Accordion
      expanded={isActive}
      onChange={handleToggle}
      className={classes.accordionItem}
      elevation={0}
    >
      <AccordionSummary
        expandIcon={
          <Icon
            className={clsx(classes.expandIcon)}
            name="expand-more-yellow"
          />
        }
        className={classes.accordionSummary}
      >
        <Text
          className={classes.title}
          variant="bodyM"
          weight="bold"
          dangerouslySetInnerHTML={{ __html: question }}
        />
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Text
          className={classes.description}
          variant="bodyM"
          align="left"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </AccordionDetails>
    </Accordion>
  )
})

AccordionItem.propTypes = {
  question: PropTypes.string,
  answer: PropTypes.string,
  info: PropTypes.object,
  classes: PropTypes.object,
}

AccordionItem.displayName = "AccordionItem"

const SupportModule = props => {
  const { item, secondary, title, info } = props
  const classes = useStyles()

  const filteredAccordions = React.useMemo(
    () =>
      (item?.field_value || []).filter(
        accordion => accordion.field_value?.question?.field_value
      ),
    [item]
  )

  return (
    <Module className={classes.supportModule}>
      <Box className={classes.containerSize}>
        <Text
          className={classes.mainTitle}
          variant="titleXXXL"
          weight="bold"
          component="h3"
          align="center"
          overline
        >
          {title.field_value}
        </Text>

        <div className={classes.accordionsContainer}>
          {filteredAccordions.map((accordion, i) => (
            <AccordionItem
              question={accordion.field_value?.question?.field_value}
              answer={accordion.field_value?.answer?.field_value}
              info={info}
              classes={classes}
              key={i}
            />
          ))}
        </div>

        {secondary?.field_value && secondary.field_value.length > 0 && (
          <div className={classes.iconsContainer}>
            {secondary.field_value.map((second, i) => (
              <div className={classes.phoneSpacing} key={i}>
                <Link to={second.field_value?.cta?.field_value?.url}>
                  <div className={classes.secondaryContainer}>
                    <FluidImage
                      className={classes.secondaryIcon}
                      {...second.field_value?.image?.field_value}
                    />
                    <Text
                      className={classes.secondaryTitle}
                      variant="bodyExtS"
                      weight="bold"
                    >
                      {second.field_value?.cta?.field_value?.label}
                    </Text>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </Box>
    </Module>
  )
}

SupportModule.propTypes = {
  item: PropTypes.shape({
    field_value: PropTypes.arrayOf(PropTypes.object),
    field_type: PropTypes.string,
  }),
  secondary: PropTypes.shape({
    field_value: PropTypes.arrayOf(PropTypes.object),
    field_type: PropTypes.string,
  }),
  title: PropTypes.shape({
    field_type: PropTypes.string,
    field_value: PropTypes.string,
  }),
  info: PropTypes.object,
}

export default SupportModule
